/**
 * Routes where third-party trackers must NOT load.
 *
 * The platform loads two things for every visitor from the root providers: the TikTok advertising
 * pixel, and LogRocket session replay. Neither is gated on consent, because until now every page that
 * loaded them sat behind a login, where a signed-in user has accepted terms.
 *
 * `/try/*` (Experiment 4) breaks that assumption. It is public, its whole audience arrives signed out
 * from a search result, and it is built to receive volume. Session replay on an anonymous visitor who
 * has agreed to nothing, plus an ad-network pixel firing on the same page, is the exact shape of the
 * CIPA-style claims the privacy review flagged.
 *
 * 🔴 THIS NARROWS THE EXPOSURE, IT DOES NOT REMOVE IT. Segment still loads on these routes, because
 * the experiment's own `labs_preview_*` events are the entire measurement. Segment is first-party
 * product analytics with no replay and no ad network attached, which is a different risk from a
 * session recorder and a pixel, but it is not zero. The real answer is a consent manager, which is
 * Phase 2 of the privacy work. This is the stopgap that lets Experiment 4 ship without widening the
 * problem.
 *
 * Kept as a list rather than inverted (an allowlist of tracked routes) on purpose: the default has to
 * stay "tracking loads", so a new authenticated page cannot silently lose its analytics by omission.
 */

/** Route prefixes that load no third-party trackers. Matched against the pathname. */
const UNTRACKED_ROUTE_PREFIXES: readonly string[] = ['/try'];

/**
 * Whether third-party trackers (the TikTok pixel, LogRocket replay) may load on this pathname.
 *
 * Returns true for an unknown pathname. `usePathname` can be null during the first client render, and
 * defaulting to "no tracking" there would silently drop the pixel across the whole authenticated app
 * for one frame on every load, which is a far worse failure than the one this guards.
 */
export function allowsThirdPartyTracking(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return true;
  }

  return !UNTRACKED_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
